import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { useParams } from 'react-router-dom';
import { useLoanContext } from '../LoanContext';
import { formatDateToDDMMYYYY } from '../../../utils/dateUtils';
import { formatAmount } from '../../../utils/formatters';
import PrimaryButton from '../../../components/styled/PrimaryButton';
import { styled } from '@mui/material/styles';
import TableWithPagination from '../../../components/TableWithPagination';

const FooterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

interface LoanStatementRow {
  transactionDate: string | null;
  interestAccrued: number | null;
  disbursement: number | null;
  fee: number | null;
  payment: number | null;
  reversal: number | null;
  adjustment: number | null;
  excess: number | null;
  closingBalance: number | null;
  transactionType: string | null;
}

interface LoanStatementResponse {
  transactions: LoanStatementRow[];
  lastRow: LoanStatementRow | null;
}

const LoanStatementTab: React.FC = () => {
  const { loan } = useLoanContext();
  const loanId = useParams().loanId;
  const [data, setData] = useState<LoanStatementResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const rowsPerPage = 20;

  useEffect(() => {
    if (!loanId || !loan) {
      setError(!loanId ? 'No loanId provided.' : 'Loading loan data...');
      setLoading(false);
      return;
    }

    const fetchLoanStatement = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(`/api/transactions/loan-statement?loanId=${loanId}`, {
          withCredentials: true,
        });
        setData(response.data);
      } catch (err: unknown) {
        setError(
          String(
            (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
              'Failed to fetch loan statement.'
          )
        );
      } finally {
        setLoading(false);
      }
    };

    fetchLoanStatement();
  }, [loanId, loan]);

  // Combine transactions with last row
  const allRows = React.useMemo(() => {
    if (!data) return [];
    const rows = [...data.transactions];
    if (data.lastRow) {
      rows.push(data.lastRow);
    }
    return rows;
  }, [data]);

  const formatValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '—';
    const numValue = parseFloat(String(value));
    return isNaN(numValue) ? '—' : formatAmount(numValue);
  };

  const handleDownload = async () => {
    setError('');

    if (!loanId) {
      setError('Loan Account Id is required to download PDF.');
      return;
    }

    try {
      const accountNumber = loan?.name || loanId || 'Unknown';
      const todayDate = formatDateToDDMMYYYY(new Date());
      const baseFileName = `${accountNumber} - Loan Statement - ${todayDate}`;
      const endpoint = `/api/loans/${encodeURIComponent(loanId)}/loanstatementpdfrestapi`;

      const response = await axios.get(endpoint, {
        responseType: 'blob',
        withCredentials: true,
        validateStatus: () => true,
      });
      const contentType = String(response.headers?.['content-type'] || '');

      if (response.status >= 400 || !contentType.toLowerCase().includes('application/pdf')) {
        throw new Error('Expected PDF response from middleware endpoint.');
      }

      saveAs(response.data, `${baseFileName}.pdf`);
    } catch {
      setError('Failed to generate PDF file. Please try again or contact support.');
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" mt={8}>
        <CircularProgress />
        <Typography sx={{ mt: 2, color: "#fff" }}>Loading loan statement...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" mt={8}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: { xs: '100%', lg: 'calc(100% + 220px)' },
        ml: { xs: 0, lg: '-110px' },
        mr: { xs: 0, lg: '-110px' },
        maxWidth: 'none',
        px: { xs: 0, md: 1 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          borderRadius: 2,
          // Keep this styling scoped to Loan Statement only.
          '& .MuiTableCell-root': {
            px: { xs: '10px', md: '12px' },
          },
        }}
      >
        <TableWithPagination
          columns={[
            {
              label: 'Transaction Date',
              key: 'transactionDate',
              sortable: true,
              renderCell: (row: Record<string, unknown>) =>
                row.transactionDate ? formatDateToDDMMYYYY(String(row.transactionDate)) : '—',
            },
            {
              label: 'Interest Accrued',
              key: 'interestAccrued',
              align: 'right',
              renderCell: (row: Record<string, unknown>) => formatValue(row.interestAccrued as number | null | undefined),
            },
            {
              label: 'Disbursement',
              key: 'disbursement',
              align: 'right',
              renderCell: (row: Record<string, unknown>) => formatValue(row.disbursement as number | null | undefined),
            },
            {
              label: 'Fee',
              key: 'fee',
              align: 'right',
              renderCell: (row: Record<string, unknown>) => formatValue(row.fee as number | null | undefined),
            },
            {
              label: 'Payment',
              key: 'payment',
              align: 'right',
              renderCell: (row: Record<string, unknown>) => formatValue(row.payment as number | null | undefined),
            },
            {
              label: 'Reversal',
              key: 'reversal',
              align: 'right',
              renderCell: (row: Record<string, unknown>) => formatValue(row.reversal as number | null | undefined),
            },
            {
              label: 'Adjustment',
              key: 'adjustment',
              align: 'right',
              renderCell: (row: Record<string, unknown>) => formatValue(row.adjustment as number | null | undefined),
            },
            {
              label: 'Excess',
              key: 'excess',
              align: 'right',
              renderCell: (row: Record<string, unknown>) => formatValue(row.excess as number | null | undefined),
            },
            {
              label: 'Closing Balance',
              key: 'closingBalance',
              align: 'right',
              renderCell: (row: Record<string, unknown>) => formatValue(row.closingBalance as number | null | undefined),
            },
            {
              label: 'Transaction Type',
              key: 'transactionType',
              renderCell: (row: Record<string, unknown>) => (row.transactionType as string) || '—',
            },
          ]}
          rows={allRows as unknown as Record<string, unknown>[]}
          getRowKey={(row, idx) => `${String(row.transactionDate || 'na')}-${String(row.transactionType || 'na')}-${idx ?? 0}`}
          rowsPerPage={rowsPerPage}
          minTableWidth={980}
        />
      </Box>

      <FooterContainer>
        <PrimaryButton
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={!allRows || allRows.length === 0}
          sx={{ width: '469px' }}
        >
          Download
        </PrimaryButton>
      </FooterContainer>
    </Box>
  );
};

export default LoanStatementTab;
